import * as _ from 'lodash-es';

import { swapElements, moveElementToBack } from '@/utils';

import { GO } from './common';
import { EntityGroup, FittingEntity } from './entity';
import { DUCTSHAPES as SP, LENGTH_TYPE } from './options';

export const DEFAULT_ALL_SHORT = 'all';
export const DEFAULT_EMPTIY_OPT = 'empty';

/**
 * TODO: `s_number` may not useful anymore... @2024/11/12
 */
export const fieldsExcludeFromCompletion = [
  's_number', // this is a field of `EQUIPMENT` symbol, ignored to calculate completion
  'zone', // @2025/04/03
];

// === editable fields ===
export const FIELD_W1 = 'width_1';
export const FIELD_W2 = 'width_2';
export const FIELD_H1 = 'height_1';
export const FIELD_H2 = 'height_2';
export const FIELD_D1 = 'diameter_1';
export const FIELD_D2 = 'diameter_2';
export const FIELD_D3 = 'diameter_3';
export const FIELD_D4 = 'diameter_4';

/** real length of a duct fitting */
export const FIELD_L0 = 'length';
/** from EQUIPMENT symbol */
export const FIELD_ID = 'identifier';
/** from EQUIPMENT sybmol */
export const FIELD_ST = 'subtype';
/** from EQUIPMENT sybmol */
export const FIELD_SN = 's_number';
/** system field - @2025/04/03 */
export const FIELD_S0 = 'system';
/** zone field for all symbol - @2025/04/03 */
export const FIELD_Z0 = 'zone';
/** shape field */
export const FIELD_SP = 'shape';
/** text content field - @2025/10/08 */
export const FIELD_CT = 'content';
/** multiplier for equipment - @2025/10/17 */
export const FIELD_MP = 'multiplier';
/** name for equipment - @2025/10/20 */
export const FIELD_NM = 'name';

// === read-only fields ===
export const LENGTH_FIELD = 'length_1';
export const LENGTH_STR_FIELD = 'length_unit_str';
/**
 * fields no need to show at properties panel
 */
export const LENGTH_FIELDS = ['length_1'];
export const USER_VALIDATED_FIELD = 'user_validated';
export const VERTICAL_LENGTH_FIELD = 'vertical_length'; // @2024/10/29

/** invisible fields */
export const FIELD_HAS_EC = 'has_endcap';

// for pipe fitting such as elbow-pipe
// @2025/08/19
export const ANGLE_VALUES = ['90', '60', '45', '30'];

/**
 * == Used to check if a property is valid `numeric` dimension field through: ==
 * for `isDimensionField` function
 * Define all the fields used in fittings and pipes...
 * As a field name standard/dict used in codes
 * TODO: Add more fields ...
 */
export enum FIXTUREATTRS {
  D1 = FIELD_D1,
  D2 = FIELD_D2,
  D3 = FIELD_D3,
  D4 = FIELD_D4,
  H1 = FIELD_H1,
  H2 = FIELD_H2,
  W1 = FIELD_W1,
  W2 = FIELD_W2,
  VL = VERTICAL_LENGTH_FIELD,
  /** for `Silencer` symbol - @2025/03/07 */
  L0 = FIELD_L0,
}

/**
 * Include all the `string` dimension for review table input
 * FIXME: hard-coded fields from `EQUIPMENT` - @2023/11/17
 * FIXME: add `diffuser_code` for `SUPPLY_AIR_DIFFUSER` - @2024/11/12
 * FIMXE: add `identifier` for `EQUIPMENT` - @2024/12/10
 * FIXME: add `product_code` for TOILET symbol - @2025/01/17
 * FIXME: add `length_unit_str` for `duct_length` - @2025/03/11
 * FIXME: add `zone` for all symbol - @2025/06/27
 */
export const stringTypeFields = [
  'name',
  'subtype',
  's_number',
  'diffuser_code',
  'identifier',
  'product_code',
  'length_unit_str',
  'zone',
];

export const readonlyFields = [LENGTH_STR_FIELD];

export const coarseUnitSuffix = [VERTICAL_LENGTH_FIELD, FIELD_L0];

/**
 * FIXME: hard-coded round shape ducts used in new duct dialog
 * @date 2024/03/20
 */
export const roundDucts = ['ROUND_DUCT_UP', 'ROUND_DUCT_DOWN'];

/**
 * fields no need to edit
 * @2025/04/22
 */
export const OMIT_FROM_LENGTH = [LENGTH_FIELD, LENGTH_STR_FIELD];

export const OMIT_FROM_ROUND = [
  'height',
  'height_1',
  'height_2',
  'height_3',
  'width',
  'width_1',
  'width_2',
  'width_3',
  `length_1`,
];

export const OMIT_FROM_SQUARE = [
  'angle', // FIXME: add `angle` for `ELBOW_PIPING` symbol @2024/07/02
  'diameter',
  'diameter_1',
  `diameter_2`,
  `diameter_3`,
  `length_1`,
  // ! do not make sense ... @2025/02/27
  // `vertical_length`, // for `ENDPOINT_TYPE_A` @2025/01/14
];

/**
 * fields doesnt needed in `ROUNDRECT` shape
 * @2025/09/08
 */
export const OMIT_FROM_ROUNDRECT = [
  'angle',
  'diameter',
  'height',
  'width',
  `diameter_2`,
  `diameter_3`,
  'height_2',
  'height_3',
  'width_2',
  'width_3',
  `length_1`,
];

/**
 * ! Omited dimensions from `round-rect` shape of _REDUCER
 * @2025/07/16
 */
export const OMIT_FROM_RDRC = ['height_2', 'width_2', 'diameter_2', 'length_1'];

/**
 * check shape options in side panel
 * @2025/07/03
 */
export const findShapeOptionsBy = (entityType: string) => {
  if (entityType.includes('_REDUCER')) {
    return [SP.ROUND, SP.SQUARE, SP.RNDREC];
  }
  return [SP.ROUND, SP.SQUARE];
};

export const hasRoundRect = (entityType: string) => {
  const isReducer = entityType.includes('_REDUCER');
  return isReducer;
};

/**
 * Move target field to the bottom of field list
 * @data 2025/04/03
 * @param fields list of symbol property
 * @param targetField some field need to move bottom
 * @returns re-ordered fields
 */
export const putFieldToBottom = (fields: string[], targetField: string) => {
  const fieldIndex = fields.indexOf(targetField);
  if (fieldIndex > -1) return moveElementToBack(fields, fieldIndex);
  return fields;
};

/**
 * part of pipes function to handl equipment symbol display in side panel
 * @date 2025/10/22
 * @param fields
 * @returns
 */
export const pushZoneTOBottom = (fields: string[]) => {
  const fieldIndex = fields.indexOf(FIELD_Z0);
  if (fieldIndex > -1) return moveElementToBack(fields, fieldIndex);
  return fields;
};

/**
 * re-order EQUIPMENT symbol fields by:
 * [name, multiplier... subtype, identifier, ...]
 *
 * @log add `multiplier` 2025/10/20
 * @param dimensionForType fields for equipment symbol
 * @returns
 */
export const reOrderEquipmentFields = (dimensionForType: string[]) => {
  const reOrderedFields = [
    FIELD_NM,
    FIELD_ST,
    FIELD_ID,
    FIELD_SN,
    FIELD_Z0,
    FIELD_MP,
  ];
  // find the diff to add to the bottom
  const diffFields = _.difference(dimensionForType, reOrderedFields);
  const mergedFields = reOrderedFields.concat(diffFields);
  return mergedFields;
};

/**
 * put `diameter_2` to the end of list
 * put 3 diameters with: 1, 2, 3, 4 - @2025/05/28, 29
 * TODO: MORE...
 * @param dimensionForType
 * @returns
 */
export const reOrderFieldsForDiameters = (dimensionForType: string[]) => {
  const diameter2 = dimensionForType.find((f) => f === FIELD_D2);
  const diameter3 = dimensionForType.find((f) => f === FIELD_D3);
  const diameter4 = dimensionForType.find((f) => f === FIELD_D4);

  let middleResult = dimensionForType;
  if (diameter2) {
    middleResult = putFieldToBottom(middleResult, diameter2);
  }
  if (diameter3) {
    middleResult = putFieldToBottom(middleResult, diameter3);
  }
  if (diameter4) {
    middleResult = putFieldToBottom(middleResult, diameter4);
  }
  return middleResult;
};

/**
 * - put `length_unit_str` to the back of `width_1`
 * @date 2024/08/27
 * - put `width_1` ahead of `height_1`
 * @date 2025/01/28
 */
export const reOrderFieldsForLength = (dimensionForType: string[]) => {
  const lengthField = dimensionForType.findIndex((f) => f === LENGTH_STR_FIELD);
  const widthField = dimensionForType.findIndex((f) => f === FIELD_W1);
  const width2Field = dimensionForType.findIndex((f) => f === FIELD_W2);
  const heightField = dimensionForType.findIndex((f) => f === FIELD_H1);
  const height2Field = dimensionForType.findIndex((f) => f === FIELD_H2);
  const vlIndex = dimensionForType.indexOf(VERTICAL_LENGTH_FIELD);
  const lIndex = dimensionForType.indexOf(FIELD_L0);

  let fields4Length = dimensionForType;

  if (lengthField > -1 && widthField > -1 && heightField > -1) {
    const firstSwap = swapElements(dimensionForType, heightField, widthField);
    fields4Length = swapElements(firstSwap, lengthField, widthField);
  }
  // only exits: `width_1` && `height_1`
  // @2025/05/07
  if (
    widthField > -1 &&
    heightField > -1 &&
    width2Field == -1 &&
    height2Field == -1
  ) {
    fields4Length = swapElements(dimensionForType, heightField, widthField);
  }

  // exists: `width_1` && `height_1` && `width_2` && `height_2`
  // h1, h2, w1, w2 --> w1, w2, h1, h2
  // @2025/05/07
  if (
    widthField > -1 &&
    heightField > -1 &&
    width2Field > -1 &&
    height2Field > -1
  ) {
    const w2first = swapElements(dimensionForType, height2Field, width2Field);
    fields4Length = swapElements(w2first, heightField, widthField);
  }

  if (vlIndex > -1) {
    fields4Length = moveElementToBack(fields4Length, vlIndex);
  }
  if (lIndex > -1) {
    fields4Length = moveElementToBack(fields4Length, lIndex);
  }

  return fields4Length;
};

/**
 * Check if the duct is round, then display width/height input only in new duct dialog
 *
 * @date 2024/03/21
 * @param itemType
 * @returns
 */
export const checkDuctRoundOnly = (itemType: string) => {
  return roundDucts.includes(itemType);
};

/**
 * Exclude the unrelevant fields in calculation of completion
 *
 * @param fields dimension fields to calculate completion
 *
 * @returns necessary fields to calculate
 */
export const filterCompletionFields = (fields: string[]) => {
  return _.difference(fields, fieldsExcludeFromCompletion);
};

/**
 * Back compatible shape checking method for `round|ROUND`
 * @param shape
 * @returns
 */
export const isRoundShape = (shape: string | undefined) => {
  return (
    shape == SP.ROUND || shape?.toUpperCase() == SP.ROUND || shape == 'Round'
  );
};

/**
 * Back compatible shape checking method for `rect|RECT|rectangular`
 * @param shape
 * @returns
 */
export const isRectShape = (shape: string | undefined) => {
  return (
    shape == SP.SQUARE ||
    shape?.toUpperCase() == SP.SQUARE ||
    shape == 'rectangular' ||
    shape == 'Rectangular'
  );
};

/**
 * Back compatible shape checking method for `ROUNDRECT|roundrect|round-rect`
 * @param shape
 * @returns
 */
export const isRoundRectShape = (shape: string | undefined) => {
  return (
    shape == SP.RNDREC ||
    shape?.toUpperCase() == SP.RNDREC ||
    shape == 'round-rect' // back-compatible
  );
};

/**
 * Safe way to check shape relevant field
 *
 * @param shape could be capitalized format, but no problem
 * @param field
 * @returns if field editable
 */
export const checkFieldBelongBy = (
  shape: string | undefined,
  field: string,
) => {
  if (!shape) return true; // assume its a string field and editable
  if (field === 'system') return true;
  const notFoundInSquare = !OMIT_FROM_ROUND.includes(field);
  const notFoundInRound = !OMIT_FROM_SQUARE.includes(field);
  // shape irrelavant field assume its visible! - 2025/02/27
  if (notFoundInRound && notFoundInSquare) return true;
  if (isRoundShape(shape)) return OMIT_FROM_SQUARE.includes(field);
  if (isRectShape(shape)) return OMIT_FROM_ROUND.includes(field);
  // ! consider ROUNDRECT shape case:
  // @2025/09/08
  if (isRoundRectShape(shape)) {
    return !OMIT_FROM_ROUNDRECT.includes(field);
  }
  return true; // visible to allow edit
};

/**
 * Filter out unrelated fields with shape
 *
 * @param fields fields to filter out
 * @param shape shape in use
 * @returns new set of field after shape filter
 */
export const getReducedFieldsByShape = (
  fields: string[],
  shape: string | undefined | '',
) => {
  if (!shape) return fields;
  let endFields = fields;
  if (isRoundShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_ROUND);
  }
  if (isRectShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_SQUARE);
  }
  if (isRoundRectShape(shape)) {
    endFields = _.difference(fields, OMIT_FROM_RDRC);
  }
  // ! remove `shape` at last
  // @2025/06/11
  return endFields.filter((f) => f !== 'shape');
};

export const getOppositeFields = (field: string) => {
  if (field.startsWith('diameter')) {
    return ['width_1', 'width_2', 'height_1', 'height_2'];
  }
  if (field.startsWith('width') || field.startsWith('height')) {
    return ['diameter_1', 'diameter_2'];
  }
  return [];
};

/**
 * Get shape related dimension fields
 * @param shape fitting shape: round, or square
 * @returns
 */
export const ductFieldsOmmitedBy = (shape: string) => {
  if (isRoundShape(shape)) return OMIT_FROM_ROUND;
  if (isRectShape(shape)) return OMIT_FROM_SQUARE;
  return [];
};

/**
 * check dimension type to decide if the input is `number` type
 * @param field
 * @returns
 */
export const isDimensionField = (field: string) => {
  const dimensions = Object.values(FIXTUREATTRS) as string[];
  return dimensions.includes(field);
};

/**
 * ! Restrict the input length in a hard way!
 * @date 2025/10/22
 * @param field field name
 * @param value field value
 * @returns pass or not
 */
export const inputLengthCheck = (field: string, value: string) => {
  if (field == FIELD_MP && value.length > 3) return false;
  return true;
};

/**
 * Build new pipe line info from active line
 *
 * @param details pipe line base info
 * @returns
 */
export const cloneLineBaseProperties = (details: GO): GO => {
  const shape = details['shape'] || '';
  const dimension: GO = { shape };
  //  FIXME: check `PIPE_LENGTH` to have diemeter property!
  // @date 2024/07/08
  const isPipeLine = details['entityType'] === LENGTH_TYPE.P;
  if (isRoundShape(shape) || isPipeLine) {
    dimension['diameter_1'] = details['diameter_1'] || '';
  }
  if (isRectShape(shape)) {
    dimension['width_1'] = details['width_1'] || '';
    dimension['height_1'] = details['height_1'] || '';
  }
  return {
    category: details['category'],
    entityType: details['entityType'],
    icon: details['icon'],
    system: details['system'],
    toolType: details['toolType'],
    ...dimension,
  };
};

/**
 * TODO: move to `dimension.ts`
 * Organize list of symbols by entity type
 *
 * @param features symbols details list
 * @param includeLength if features have `???_LENGTH` item
 *
 * @returns group symbols by entity type
 */
export const findEntityTypesFromSymbols = (
  features: GO[],
  includeLength = false,
) => {
  const groups: EntityGroup = {};

  features.forEach((f) => {
    const { entityType } = f as FittingEntity;
    // console.log(entityType);
    if (entityType === 'LABEL') return console.warn('## got LABEL!');

    const groupByType = groups[entityType];
    // blank list init
    if (!groupByType) groups[entityType] = [];
    groups[entityType].push(f);
  });

  if (includeLength) {
    return {
      groups,
      types: Object.keys(groups),
    };
  }

  const excludePipeLength = Object.keys(groups).filter(
    (t) => t !== LENGTH_TYPE.D && t !== LENGTH_TYPE.P,
  );

  return {
    groups,
    types: excludePipeLength,
  };
};

/**
 * Find the common value of each field among the symbols
 * @param symbols
 * @param fieldsAfterShapeChecking
 * @returns
 */
export const getCommonValueFromSymbols = (
  symbols: GO[],
  fieldsAfterShapeChecking: string[],
) => {
  // figure out the common values for each field
  const dimensionValuesByField: { [key: string]: Set<string> } = {};
  fieldsAfterShapeChecking.forEach((f) => {
    const values = dimensionValuesByField[f];
    if (!values) dimensionValuesByField[f] = new Set();
    symbols.forEach((detail) => dimensionValuesByField[f].add(detail[f]));
  });
  return fieldsAfterShapeChecking.reduce((prev: GO, curr: string) => {
    const values = dimensionValuesByField[curr];
    prev[curr] = values.size > 1 ? '' : [...values][0];
    return prev;
  }, {});
};

/**
 * Find out common fields derived from `shape`
 * @deprecated in favor of `shape` swith inside of group - 2025/06/11
 * @param symbols
 * @param commonFields
 * @returns
 */
export const getReducedFieldsFromSymbols = (
  symbols: GO[],
  commonFields: string[],
  useSystemField = true,
) => {
  // FIXME: get fields without `length_xxx` field
  // cos `length_xxx` field has no need to correct!
  // @2023/11/17
  const fieldsWithoutLengthX = commonFields.filter(
    (f) => !f.startsWith('length_'),
  );
  const correctedFields = useSystemField
    ? ['system', ...fieldsWithoutLengthX]
    : fieldsWithoutLengthX;
  const shapesFromSymbols = new Set<string>();
  symbols.forEach((smb) => smb.shape && shapesFromSymbols.add(smb.shape));
  if (shapesFromSymbols.size == 0) return correctedFields;

  const theCommonShape = shapesFromSymbols.values().next().value;
  const fieldsFromShape = getReducedFieldsByShape(
    correctedFields,
    theCommonShape,
  );
  return fieldsFromShape;
};

/**
 * filter out length fields
 * @param fields fields including: `length_1`, `length_unit_str`
 * @date 2025/06/11
 * @returns
 */
export const stripLengthFields = (fields: string[]) => {
  return fields.filter((f) => !f.startsWith('length_'));
};
